import React, { useState, useRef } from 'react';
import '../pages/PracticesPage.css';

// The first two steps of the New Practice workflow: practice info (with the logo) and
// integrations. Rendered inside PracticeSetupWizard, which owns the step bar and the
// window; ``step`` is 1-2. The practice is created when integrations is submitted, and
// onCreated(practice, logoFile) hands it to the wizard's remaining steps. Providers are a
// later step, after the EMR pull, so they can be checked against the data.
const API_URL = process.env.REACT_APP_API_URL || '';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
];

const EMPTY_FORM = {
  name: '', npi: '', address1: '', city: '', state: '', zip: '',
  phone: '', tax_id: '',
  clearinghouse: '', clearinghouse_other: '', clearinghouse_credentials: {},
  emr_system: '', emr_other: '', emr_credentials: {},
  pm_system: '', pm_other: '', pm_credentials: {},
};

export const PROVIDER_TYPES = [
  'MD', 'NP', 'APRN', 'PA', 'PT', 'OT', 'DC',
  'SLP', 'LCSW', 'Psychologist', 'RD', 'DPM', 'Other',
];

// Dropdown options. Keys marked with hasApi trigger credential-field prompts.
const INTEGRATION_OPTIONS = {
  clearinghouse: [
    { value: 'waystar', label: 'Waystar' },
    { value: 'change', label: 'Change' },
    { value: 'trizetto', label: 'TriZetto' },
    { value: 'officeally', label: 'Office Ally' },
    { value: 'claimmd', label: 'Claim.MD', hasApi: true },
    { value: 'stedi', label: 'Stedi', hasApi: true },
    { value: 'other', label: 'Other' },
  ],
  emr: [
    // Report uploads on the Data page today (docs/advancedmd.md); direct connection in progress.
    { value: 'advancedmd', label: 'AdvancedMD' },
    { value: 'athena', label: 'Athena' },
    { value: 'ecw', label: 'eClinicalWorks', hasApi: true },
    { value: 'practicefusion', label: 'Practice Fusion', hasApi: true },
    { value: 'tebra', label: 'Tebra', hasApi: true },
    // Login stored on the practice; the Kinnser daily sync pushes claims and visits in.
    { value: 'kinnser', label: 'Kinnser / WellSky', hasApi: true },
    { value: 'modmed', label: 'ModMed' },
    { value: 'other', label: 'Other' },
  ],
  pm: [
    { value: 'practicesuite', label: 'PracticeSuite', hasApi: true },
    { value: 'openpm', label: 'OpenPM' },
    { value: 'advancedmd', label: 'AdvancedMD' },
    { value: 'other', label: 'Other' },
  ],
};

function hasApiIntegration(group, value) {
  return INTEGRATION_OPTIONS[group].some(o => o.value === value && o.hasApi);
}

function formatTaxId(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 2) return digits.slice(0, 2) + '-' + digits.slice(2);
  return digits;
}

function formatZip(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 5) return digits.slice(0, 5) + '-' + digits.slice(5);
  return digits;
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length >= 7) return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
  if (digits.length >= 4) return digits.slice(0, 3) + '-' + digits.slice(3);
  return digits;
}

export function validateNpi(npi) {
  if (!npi) return null;
  const digits = npi.replace(/\D/g, '');
  if (digits.length !== 10) return 'NPI must be exactly 10 digits';
  // Luhn check with CMS-mandated 80840 prefix
  const full = '80840' + digits;
  let sum = 0;
  for (let i = full.length - 1; i >= 0; i--) {
    let d = parseInt(full[i], 10);
    if ((full.length - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  if (sum % 10 !== 0) return 'Invalid NPI (failed check digit)';
  return null;
}

function NewPracticeForm({ step, setStep, onCancel, onCreated, onPracticesUpdated }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  // Uploaded once the practice exists (the logo route needs its id).
  const [logoFile, setLogoFile] = useState(null);
  const logoInputRef = useRef(null);
  const [credSchemas, setCredSchemas] = useState({ clearinghouse: null, emr: null, pm: null });

  const groupToSystemField = { clearinghouse: 'clearinghouse', emr: 'emr_system', pm: 'pm_system' };
  const groupToCredsField = { clearinghouse: 'clearinghouse_credentials', emr: 'emr_credentials', pm: 'pm_credentials' };

  const handleIntegrationChange = async (group, value) => {
    const systemField = groupToSystemField[group];
    const credsField = groupToCredsField[group];
    setForm(f => ({ ...f, [systemField]: value, [credsField]: {} }));
    setCredSchemas(s => ({ ...s, [group]: null }));
    if (hasApiIntegration(group, value)) {
      try {
        const response = await fetch(`${API_URL}/api/credential-schema/${value}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        setCredSchemas(s => ({ ...s, [group]: data.fields }));
      } catch (err) {
        setError(`Could not load credential fields for ${value}. Refresh and try again.`);
      }
    }
  };

  const handleCredFieldChange = (group, key, value) => {
    const credsField = groupToCredsField[group];
    setForm(f => ({ ...f, [credsField]: { ...f[credsField], [key]: value } }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'npi') setForm({ ...form, npi: value.replace(/\D/g, '').slice(0, 10) });
    else if (name === 'tax_id') setForm({ ...form, tax_id: formatTaxId(value) });
    else if (name === 'zip') setForm({ ...form, zip: formatZip(value) });
    else if (name === 'phone') setForm({ ...form, phone: formatPhone(value) });
    else setForm({ ...form, [name]: value });
  };

  const validateBasicInfo = () => {
    if (!form.name.trim()) return 'Practice name is required';
    if (!form.address1.trim()) return 'Address is required';
    if (!form.city.trim()) return 'City is required';
    if (!form.state) return 'State is required';
    const zipDigits = form.zip.replace(/\D/g, '');
    if (!zipDigits || (zipDigits.length !== 5 && zipDigits.length !== 9)) return 'ZIP must be 5 or 9 digits';
    const npiErr = validateNpi(form.npi);
    if (npiErr) return `Organization NPI: ${npiErr}`;
    if (form.tax_id && form.tax_id.replace(/\D/g, '').length !== 9) return 'Tax ID must be 9 digits';
    return null;
  };

  // Catches the bug where a typed API key disappeared because the dropdown
  // wasn't on a hasApi value — or where the credential field never rendered
  // because the schema fetch failed silently.
  const validateCredentials = () => {
    const groups = [
      { group: 'clearinghouse', systemField: 'clearinghouse', credsField: 'clearinghouse_credentials' },
      { group: 'emr', systemField: 'emr_system', credsField: 'emr_credentials' },
      { group: 'pm', systemField: 'pm_system', credsField: 'pm_credentials' },
    ];
    for (const { group, systemField, credsField } of groups) {
      const selected = form[systemField];
      if (!hasApiIntegration(group, selected)) continue;
      const label = INTEGRATION_OPTIONS[group].find(o => o.value === selected)?.label || selected;
      const schema = credSchemas[group];
      if (!schema) {
        return `${label} credential fields didn't load. Re-select the integration to try again.`;
      }
      const creds = form[credsField] || {};
      const missing = schema
        .filter(f => f.required && !(creds[f.key] || '').trim())
        .map(f => f.label);
      if (missing.length) {
        return `${label} requires: ${missing.join(', ')}.`;
      }
    }
    return null;
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    setError(null);
    const err = validateBasicInfo();
    if (err) { setError(err); return; }
    setStep(2);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    const basicErr = validateBasicInfo();
    if (basicErr) { setError(basicErr); setStep(1); return; }
    const credErr = validateCredentials();
    if (credErr) { setError(credErr); setStep(2); return; }

    const resolveSystem = (sel, other) => {
      if (!sel) return null;
      if (sel === 'other') return (other || '').trim() || null;
      return sel;
    };
    const clearinghouse = resolveSystem(form.clearinghouse, form.clearinghouse_other);
    const emrSystem = resolveSystem(form.emr_system, form.emr_other);
    const pmSystem = resolveSystem(form.pm_system, form.pm_other);

    const hasAnyValue = (obj) => Object.values(obj || {}).some(v => (v || '').toString().trim());

    const payload = {
      name: form.name, npi: form.npi, address1: form.address1, city: form.city,
      state: form.state, zip: form.zip, phone: form.phone, tax_id: form.tax_id,
      clearinghouse, emr_system: emrSystem, pm_system: pmSystem,
    };
    if (hasApiIntegration('clearinghouse', form.clearinghouse) && hasAnyValue(form.clearinghouse_credentials)) {
      payload.clearinghouse_credentials = form.clearinghouse_credentials;
    }
    if (hasApiIntegration('emr', form.emr_system) && hasAnyValue(form.emr_credentials)) {
      payload.emr_credentials = form.emr_credentials;
    }
    if (hasApiIntegration('pm', form.pm_system) && hasAnyValue(form.pm_credentials)) {
      payload.pm_credentials = form.pm_credentials;
    }

    setCreating(true);

    try {
      const response = await fetch(`${API_URL}/api/practices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      // A non-JSON answer (the dev proxy's "Proxy error", a gateway page) means the server
      // was not reached; say that rather than a JSON parse error.
      const data = await response.json().catch(() => ({ error: 'Could not reach the Atlas server. Try again in a moment.' }));
      if (response.ok) {
        // Fire-and-forget initial sync if an integrated clearinghouse was set
        // up — saves the user a manual click after onboarding.
        if (['claimmd', 'stedi'].includes(data.clearinghouse) && data.has_clearinghouse_credentials) {
          fetch(`${API_URL}/api/practices/${data.id}/clearinghouse/sync`, {
            method: 'POST',
            credentials: 'include',
          }).catch(() => { /* user will see status on the practice page */ });
        }
        if (onPracticesUpdated) onPracticesUpdated();
        onCreated(data, logoFile);
      } else {
        setError(data.error || 'Failed to create practice');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
        {error && (
          <div className="form-message error dismissable">
            <span className="message-content">{error}</span>
            <button type="button" className="message-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleNextStep}>
            <div className="form-row">
              <div className="form-field full">
                <label>Practice Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="e.g., Sunrise Physical Therapy" required autoFocus />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Organization NPI</label>
                <input name="npi" value={form.npi} onChange={handleChange} placeholder="10 digits" maxLength={10} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field full">
                <label>Address *</label>
                <input name="address1" value={form.address1} onChange={handleChange} placeholder="Street address" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>City *</label>
                <input name="city" value={form.city} onChange={handleChange} required />
              </div>
              <div className="form-field small">
                <label>State *</label>
                <select name="state" value={form.state} onChange={handleChange} required>
                  <option value="">--</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field small">
                <label>ZIP *</label>
                <input name="zip" value={form.zip} onChange={handleChange} placeholder="12345" maxLength={10} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="XXX-XXX-XXXX" maxLength={12} />
              </div>
              <div className="form-field">
                <label>Tax ID</label>
                <input name="tax_id" value={form.tax_id} onChange={handleChange} placeholder="XX-XXXXXXX (optional)" maxLength={10} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field full">
                <label>Logo</label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="setup-file-input"
                  aria-label="Logo file"
                  onChange={(e) => setLogoFile((e.target.files && e.target.files[0]) || null)}
                />
                <div className="setup-file-row">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => logoInputRef.current && logoInputRef.current.click()}>
                    {logoFile ? 'Choose a different file' : 'Choose logo'}
                  </button>
                  <span className="setup-muted">{logoFile ? logoFile.name : 'PNG or JPEG (optional)'}</span>
                  {logoFile && (
                    <button type="button" className="setup-link" onClick={() => { setLogoFile(null); logoInputRef.current.value = ''; }}>Remove</button>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
              <button type="submit" className="btn btn-primary">Next</button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleCreate}>
            {[
              { group: 'clearinghouse', label: 'Clearinghouse', systemField: 'clearinghouse', otherField: 'clearinghouse_other', credsField: 'clearinghouse_credentials' },
              { group: 'emr', label: 'EMR', systemField: 'emr_system', otherField: 'emr_other', credsField: 'emr_credentials' },
              { group: 'pm', label: 'Practice Management', systemField: 'pm_system', otherField: 'pm_other', credsField: 'pm_credentials' },
            ].map((row) => {
              const selected = form[row.systemField];
              const schema = credSchemas[row.group];
              return (
                <div key={row.group} className="integration-row">
                  <div className="form-row">
                    <div className="form-field">
                      <label>{row.label}</label>
                      <select
                        value={selected}
                        onChange={(e) => handleIntegrationChange(row.group, e.target.value)}
                      >
                        <option value="">None</option>
                        {INTEGRATION_OPTIONS[row.group].map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    {selected === 'other' && (
                      <div className="form-field">
                        <label>Name</label>
                        <input
                          value={form[row.otherField]}
                          onChange={(e) => setForm(f => ({ ...f, [row.otherField]: e.target.value }))}
                          placeholder="Enter name"
                        />
                      </div>
                    )}
                  </div>
                  {hasApiIntegration(row.group, selected) && schema && schema.map(field => (
                    <div key={field.key} className="form-row">
                      <div className="form-field full">
                        <label>{field.label}{field.required ? ' *' : ''}</label>
                        <input
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={form[row.credsField][field.key] || ''}
                          onChange={(e) => handleCredFieldChange(row.group, field.key, e.target.value)}
                          placeholder={field.type === 'password' ? 'Paste key' : ''}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? 'Creating...' : 'Create practice and continue'}
              </button>
            </div>
          </form>
        )}
    </>
  );
}

export default NewPracticeForm;
